import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { adminService } from '@/services/admin-service';
import { authService } from '@/services/auth-service';
import { AppHeader } from '@/components/AppHeader';
import { InlineList, InlineListRow } from '@/components/InlineList';
import { Modal } from '@/components/Modal';
import type { AdminClassResponse } from '@/types/admin';
import type { User } from '@/types/auth';
import { Pencil, Shield, Trash2, Users } from 'lucide-react';
import './AdminClasses.css';
import './AdminModules.css';
import './AdminTiles.css';
import '../styles/common.css';

const AdminClasses: React.FC = () => {
  const [classes, setClasses] = useState<AdminClassResponse[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [user, setUser] = useState<User | null>(null);
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const [isCreating, setIsCreating] = useState(false);
  const [creatingClassId, setCreatingClassId] = useState<string | null>(null);
  const [newClassName, setNewClassName] = useState('');
  const [creatingRequireSeb, setCreatingRequireSeb] = useState(true);
  const [creatingSebConfigKey, setCreatingSebConfigKey] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  const [editingClass, setEditingClass] = useState<AdminClassResponse | null>(null);
  const [editingName, setEditingName] = useState('');
  const [editingRequireSeb, setEditingRequireSeb] = useState(false);
  const [editingSebConfigKey, setEditingSebConfigKey] = useState('');
  const [isGeneratingSebConfig, setIsGeneratingSebConfig] = useState(false);
  const [editError, setEditError] = useState<string | null>(null);
  const [isSavingEdit, setIsSavingEdit] = useState(false);

  const [deletingClass, setDeletingClass] = useState<AdminClassResponse | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  useEffect(() => {
    void authService.getCurrentUser().then(setCurrentUser);
    void fetchData();
  }, []);

  useEffect(() => {
    if (searchParams.get('create') === 'true') {
      setCreatingClassId(null);
      setNewClassName('');
      setCreatingRequireSeb(true);
      setCreatingSebConfigKey('');
      setEditError(null);
      setIsCreating(true);
      setSearchParams({}, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  const setCurrentUser = (currentUser: User | null) => {
    setUser(currentUser);
  };

  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);
      const classesData = await adminService.getAllClasses();
      setClasses(classesData);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Classes could not be loaded');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async () => {
    const name = newClassName.trim();
    if (!name) {
      setEditError('Class name is required.');
      return;
    }

    try {
      setIsSaving(true);
      setEditError(null);

      if (creatingClassId) {
        await adminService.updateClass(creatingClassId, {
          name,
          requireSafeExamBrowser: creatingRequireSeb,
          sebConfigKey: creatingRequireSeb ? creatingSebConfigKey.trim() : undefined,
        });
        setClasses(current => current.map(moduleClass => (
          moduleClass.id === creatingClassId
            ? { ...moduleClass, name, requireSafeExamBrowser: creatingRequireSeb, sebConfigKey: creatingRequireSeb ? creatingSebConfigKey.trim() : undefined }
            : moduleClass
        )));
      } else {
        const newClass = await adminService.createClassStandalone({ name });
        let createdClass = newClass;
        if (creatingRequireSeb) {
          const { configKey } = await adminService.generateSebConfigFile(newClass.id);
          createdClass = { ...newClass, requireSafeExamBrowser: true, sebConfigKey: configKey ?? '' };
        }
        setClasses(current => [...current, createdClass]);
      }

      setIsCreating(false);
      setCreatingClassId(null);
      setNewClassName('');
      setCreatingRequireSeb(true);
      setCreatingSebConfigKey('');
    } catch (err) {
      setEditError(err instanceof Error ? err.message : 'Class could not be created');
    } finally {
      setIsSaving(false);
    }
  };

  const openEdit = (moduleClass: AdminClassResponse) => {
    setEditingClass(moduleClass);
    setEditingName(moduleClass.name);
    setEditingRequireSeb(moduleClass.requireSafeExamBrowser);
    setEditingSebConfigKey(moduleClass.sebConfigKey ?? '');
    setEditError(null);
  };

  const closeEdit = () => {
    if (isSavingEdit || isGeneratingSebConfig) return;
    setEditingClass(null);
    setEditError(null);
  };

  const handleSaveEdit = async () => {
    if (!editingClass) return;
    const name = editingName.trim();
    if (!name) {
      setEditError('Class name is required.');
      return;
    }
    if (editingRequireSeb && !editingSebConfigKey.trim()) {
      setEditError('A config key must be set for Safe Exam Browser.');
      return;
    }

    try {
      setIsSavingEdit(true);
      setEditError(null);
      await adminService.updateClass(editingClass.id, {
        name,
        requireSafeExamBrowser: editingRequireSeb,
        sebConfigKey: editingRequireSeb ? editingSebConfigKey.trim() : undefined,
      });
      setClasses(current => current.map(moduleClass => (
        moduleClass.id === editingClass.id
          ? { ...moduleClass, name, requireSafeExamBrowser: editingRequireSeb, sebConfigKey: editingRequireSeb ? editingSebConfigKey.trim() : undefined }
          : moduleClass
      )));
      setEditingClass(null);
    } catch (err) {
      setEditError(err instanceof Error ? err.message : 'Class could not be saved');
    } finally {
      setIsSavingEdit(false);
    }
  };

  const handleGenerateSebConfig = async () => {
    if (!editingClass) return;

    try {
      setIsGeneratingSebConfig(true);
      setEditError(null);
      const { blob, configKey, fileName } = await adminService.generateSebConfigFile(editingClass.id);

      if (configKey) {
        setEditingRequireSeb(true);
        setEditingSebConfigKey(configKey);
        setClasses(current => current.map(moduleClass => (
          moduleClass.id === editingClass.id
            ? { ...moduleClass, requireSafeExamBrowser: true, sebConfigKey: configKey }
            : moduleClass
        )));
      }

      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      setEditError(err instanceof Error ? err.message : 'SEB configuration file could not be generated');
    } finally {
      setIsGeneratingSebConfig(false);
    }
  };

  const handleGenerateSebConfigCreate = async () => {
    if (!creatingClassId && !newClassName.trim()) {
      setEditError('Please enter a class name first to generate the SEB file.');
      return;
    }

    try {
      setIsGeneratingSebConfig(true);
      setEditError(null);
      const tempClass = creatingClassId
        ? classes.find(moduleClass => moduleClass.id === creatingClassId) ?? await adminService.createClassStandalone({ name: newClassName.trim() })
        : await adminService.createClassStandalone({ name: newClassName.trim() });
      const { blob, configKey, fileName } = await adminService.generateSebConfigFile(tempClass.id);

      if (!creatingClassId) {
        setCreatingClassId(tempClass.id);
        setClasses(current => [...current, tempClass]);
      }
      if (configKey) {
        setCreatingRequireSeb(true);
        setCreatingSebConfigKey(configKey);
        setClasses(current => current.map(moduleClass => (
          moduleClass.id === tempClass.id
            ? { ...moduleClass, requireSafeExamBrowser: true, sebConfigKey: configKey }
            : moduleClass
        )));
      }

      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      setEditError(err instanceof Error ? err.message : 'SEB configuration file could not be generated');
    } finally {
      setIsGeneratingSebConfig(false);
    }
  };

  const handleDelete = async () => {
    if (!deletingClass) return;

    try {
      setIsDeleting(true);
      setError(null);
      await adminService.deleteClass(deletingClass.id);
      setClasses(current => current.filter(moduleClass => moduleClass.id !== deletingClass.id));
      setDeletingClass(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Class could not be deleted');
    } finally {
      setIsDeleting(false);
    }
  };

  const handleViewUsers = (moduleClass: AdminClassResponse) => {
    const query = new URLSearchParams({ classId: moduleClass.id, className: moduleClass.name, from: 'classes' });
    navigate(`/admin/class-users?${query}`);
  };

  const openCreate = () => {
    setCreatingClassId(null);
    setNewClassName('');
    setCreatingRequireSeb(true);
    setCreatingSebConfigKey('');
    setEditError(null);
    setIsCreating(true);
  };

  const closeCreate = async () => {
    if (isSaving) return;
    if (creatingClassId) {
      try {
        await adminService.deleteClass(creatingClassId);
        setClasses(current => current.filter(moduleClass => moduleClass.id !== creatingClassId));
      } catch {
        // Ignorieren: Klasse bleibt bestehen, falls Löschen fehlschlägt
      }
    }
    setIsCreating(false);
    setCreatingClassId(null);
    setNewClassName('');
    setCreatingRequireSeb(true);
    setCreatingSebConfigKey('');
    setEditError(null);
  };

  return (
    <div className="admin-classes-container">
      <AppHeader user={user} title="KI Expert - Admin" />
      <main className="admin-classes-main">
        <div className="navigation-bar">
          <div className="navigation-bar-header">
            <h2 className="navigation-bar-title">Classes</h2>
          </div>
          <div className="navigation-bar-actions">
            <button className="btn btn-success" onClick={openCreate} type="button">Add Class</button>
          </div>
        </div>

        {loading ? <div className="loading-container"><div className="loading-spinner" /><p>Loading classes...</p></div>
          : error ? <div className="error-container"><h3>Error loading</h3><p>{error}</p><button className="btn btn-primary" onClick={() => void fetchData()} type="button">Try again</button></div>
          : classes.length === 0 ? <div className="empty-container"><Users aria-hidden="true" size={40} /><h3>No classes found</h3><p>Create a new class to invite users.</p></div>
          : <InlineList compact>{classes.map(moduleClass => (
            <InlineListRow
              key={moduleClass.id}
              actions={(
                <>
                  <button aria-label={`View users of ${moduleClass.name}`} className="class-action" data-tooltip="Users" onClick={() => handleViewUsers(moduleClass)} type="button"><Users aria-hidden="true" size={18} /></button>
                  <button aria-label={`Edit ${moduleClass.name}`} className="class-action" data-tooltip="Edit" onClick={() => openEdit(moduleClass)} type="button"><Pencil aria-hidden="true" size={18} /></button>
                  <button aria-label={`Delete ${moduleClass.name}`} className="class-action class-action-danger" data-tooltip="Delete" onClick={() => setDeletingClass(moduleClass)} type="button"><Trash2 aria-hidden="true" size={18} /></button>
                </>
              )}
            >
              <div className="admin-class-row">
                <div className="admin-class-info">
                  <h3>{moduleClass.name}</h3>
                  <div className="admin-class-meta">
                    {moduleClass.requireSafeExamBrowser && <span className="admin-class-seb"><Shield aria-hidden="true" size={14} /> SEB</span>}
                  </div>
                </div>
              </div>
            </InlineListRow>
          ))}</InlineList>}
      </main>

      {isCreating && <Modal
        footer={<><button className="btn btn-secondary" disabled={isSaving || isGeneratingSebConfig} onClick={() => void closeCreate()} type="button">Cancel</button><button className="btn btn-primary" disabled={isSaving || isGeneratingSebConfig} onClick={() => void handleCreate()} type="button">{isSaving ? 'Creating...' : 'Create'}</button></>}
        onClose={() => !isSaving && !isGeneratingSebConfig && void closeCreate()}
        title="Add Class"
      >
        {editError && <p className="modal-error">{editError}</p>}
        <div className="modal-field">
          <label htmlFor="new-class-name">Class name</label>
          <input id="new-class-name" onChange={event => setNewClassName(event.target.value)} value={newClassName} />
        </div>
        <div className="modal-field modal-field-checkbox">
          <label htmlFor="new-class-require-seb">
            <input checked={creatingRequireSeb} id="new-class-require-seb" onChange={event => setCreatingRequireSeb(event.target.checked)} type="checkbox" />
            Safe Exam Browser required
          </label>
        </div>
        <div className="modal-field">
          <label htmlFor="new-class-seb-key">SEB config key</label>
          <input id="new-class-seb-key" readOnly value={creatingSebConfigKey || 'No configuration file generated yet'} />
          <button className="btn btn-secondary" disabled={isGeneratingSebConfig} onClick={() => void handleGenerateSebConfigCreate()} type="button">
            {isGeneratingSebConfig ? 'Generating...' : 'Generate & download SEB configuration file'}
          </button>
          <p className="modal-field-hint">Creates a .seb file for this class and automatically sets the corresponding config key.</p>
        </div>
      </Modal>}

      {editingClass && <Modal
        footer={<><button className="btn btn-secondary" disabled={isSavingEdit || isGeneratingSebConfig} onClick={closeEdit} type="button">Cancel</button><button className="btn btn-primary" disabled={isSavingEdit || isGeneratingSebConfig} onClick={() => void handleSaveEdit()} type="button">{isSavingEdit ? 'Saving...' : 'Save'}</button></>}
        onClose={closeEdit}
        title="Edit class"
      >
        {editError && <p className="modal-error">{editError}</p>}
        <div className="modal-field">
          <label htmlFor="class-name">Class name</label>
          <input id="class-name" onChange={event => setEditingName(event.target.value)} value={editingName} />
        </div>
        <div className="modal-field modal-field-checkbox">
          <label htmlFor="class-require-seb">
            <input checked={editingRequireSeb} id="class-require-seb" onChange={event => setEditingRequireSeb(event.target.checked)} type="checkbox" />
            Safe Exam Browser required
          </label>
        </div>
        <div className="modal-field">
          <label htmlFor="class-seb-key">SEB config key</label>
          <input id="class-seb-key" readOnly value={editingSebConfigKey || 'No configuration file generated yet'} />
          <button className="btn btn-secondary" disabled={isGeneratingSebConfig} onClick={() => void handleGenerateSebConfig()} type="button">
            {isGeneratingSebConfig ? 'Generating...' : 'Generate & download SEB configuration file'}
          </button>
          <p className="modal-field-hint">Creates a .seb file for this class and automatically sets the corresponding config key.</p>
        </div>
      </Modal>}

      {deletingClass && <Modal
        footer={<><button className="btn btn-secondary" disabled={isDeleting} onClick={() => setDeletingClass(null)} type="button">Cancel</button><button className="btn btn-danger" disabled={isDeleting} onClick={() => void handleDelete()} type="button">{isDeleting ? 'Deleting...' : 'Delete'}</button></>}
        onClose={() => !isDeleting && setDeletingClass(null)}
        title="Delete class"
      >
        <p className="modal-confirmation-message">Should the class “{deletingClass.name}” really be deleted? It will be removed from all modules.</p>
      </Modal>}
    </div>
  );
};

export default AdminClasses;
